import { Routes } from '~/constants/routes';
import { RESOURCE_TYPE } from '~/constants/user';
import { hasAnyRole } from '~/utils/roles';

/**
 * Product analytics for Arcane.
 *
 * THE ONLY MODULE THAT KNOWS THE VENDOR. Everything else calls the functions
 * below and names no product, so swapping PostHog out means rewriting this
 * file and nothing else.
 *
 * Mirrors the privacy posture of rosalind's `useTracking.js` (the same org's
 * already-reviewed PostHog setup): identify only after pseudonymizing the
 * email, mask element text/attributes, template URLs before they leave the
 * browser, and keep every PostHog surface beyond core analytics (session
 * replay, remote feature flags, surveys, heatmaps) switched off.
 *
 * Enablement is env-only here (arcane is one tenant per deploy, unlike
 * rosalind's per-tenant config): with `VUE_APP_POSTHOG_PROJECT_ID` or
 * `VUE_APP_POSTHOG_URL` missing, tracking stays inert and every exported
 * function is a safe no-op. Restricted to staging (`STG`) and production
 * (`PROD`) — `VUE_APP_STAGE=DEV` (dev/mca) never registers, even though
 * those env files carry the same PostHog project config.
 */

// ---------------------------------------------------------------------------
// 1. THE SCRUBBER — runs on every event just before it is sent.
// ---------------------------------------------------------------------------

const UNKNOWN_PATH = '/unknown';

const URL_PROPERTIES = new Set([
  '$current_url',
  '$host',
  '$pathname',
  '$referrer',
  '$referring_domain',
  '$prev_pageview_pathname',
]);

// posthog-js builds `$initial_*` / `$session_entry_*` names by concatenation,
// so they never appear literally in its source — matched by prefix instead.
const URL_PROPERTY_PREFIXES = ['$initial_', '$session_entry_'];

/**
 * Route patterns a captured path is templated against, so `/assist/abc-123`
 * reports as `/assist/:conversationId` rather than leaking a conversation id.
 * Kept in sync by hand with `constants/routes.js` — add a pattern here
 * whenever a new param-bearing route is added there.
 */
const ROUTE_PATTERNS = [
  Routes.Dashboard,
  Routes.Conversation.path,
  Routes.Authoring.path,
  Routes.Callback,
  Routes.Home,
  Routes.Login,
  Routes.Error,
  Routes.MyCreations,
  Routes.UserImages,
];

const patternToRegExp = (pattern) => {
  const source = pattern
    .split('/')
    .filter((segment) => segment !== '')
    .map((segment) => {
      if (segment.startsWith(':')) {
        return segment.endsWith('?') ? '(?:/[^/]+)?' : '/[^/]+';
      }
      return `/${segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`;
    })
    .join('');

  return new RegExp(`^${source || '/'}/?$`);
};

const patternToTemplate = (pattern) => pattern.replace(/\?/g, '');

let routeMatchers = null;

const getRouteMatchers = () => {
  if (!routeMatchers) {
    routeMatchers = ROUTE_PATTERNS.map((pattern) => ({
      match: patternToRegExp(pattern),
      template: patternToTemplate(pattern),
    }));
  }
  return routeMatchers;
};

/** Replace a URL with the route template it corresponds to, or UNKNOWN_PATH. */
export const sanitizeUrl = (rawUrl) => {
  if (typeof rawUrl !== 'string' || rawUrl === '') {
    return UNKNOWN_PATH;
  }

  let pathname;
  try {
    pathname = new URL(rawUrl, 'http://localhost').pathname;
  } catch {
    return UNKNOWN_PATH;
  }

  const matched = getRouteMatchers().find(({ match }) => match.test(pathname));
  return matched ? matched.template : UNKNOWN_PATH;
};

const isUrlProperty = (key) =>
  URL_PROPERTIES.has(key) || URL_PROPERTY_PREFIXES.some((prefix) => key.startsWith(prefix));

/**
 * Attributes autocapture copies off the clicked element as `attr__<name>`.
 * Repeated here (in addition to `element_attribute_ignorelist` on init) so
 * the scrub still holds if that option is ever changed.
 */
const BLOCKED_ELEMENT_ATTRIBUTES = new Set([
  'attr__aria-label',
  'attr__title',
  'attr__alt',
  'attr__placeholder',
  'attr__href',
  'attr__value',
  'attr__name',
  'attr__src',
  'attr__srcset',
  // Not a standard HTML attribute — it's Vue's `v-model` fallthrough onto a
  // component's root element when the component doesn't declare a
  // `modelValue` prop (found leaking a reference's title text this way from
  // a stray `v-model` on EditableReferenceField, since fixed at the source
  // in CustomReferenceListItem.vue — kept here in case the same mistake
  // happens again elsewhere).
  'attr__modelvalue',
]);

export const sanitizeElements = (elements) => {
  if (!Array.isArray(elements)) {
    return elements;
  }

  return elements.map((element) =>
    Object.fromEntries(
      Object.entries(element ?? {}).filter(([key]) => key !== '$el_text' && !BLOCKED_ELEMENT_ATTRIBUTES.has(key)),
    ),
  );
};

// `$set`/`$set_once` carry their own copy of person properties, including
// URL-derived ones — verified against rosalind's live data. Rewriting only
// `event.properties` leaves a raw URL behind on the person record.
const PERSON_PROPERTY_CARRIERS = ['$set', '$set_once'];

const sanitizeUrlProperties = (properties) =>
  Object.fromEntries(
    Object.entries(properties).map(([key, value]) => {
      if (PERSON_PROPERTY_CARRIERS.includes(key) && value && typeof value === 'object') {
        return [key, sanitizeUrlProperties(value)];
      }
      return isUrlProperty(key) && typeof value === 'string' ? [key, sanitizeUrl(value)] : [key, value];
    }),
  );

const sanitizeEventLevelPersonProperties = (event) => {
  PERSON_PROPERTY_CARRIERS.forEach((carrier) => {
    if (event[carrier] && typeof event[carrier] === 'object') {
      event[carrier] = sanitizeUrlProperties(event[carrier]);
    }
  });
};

/**
 * What our own named events (added via `trackEvent`) are allowed to carry.
 * An allowlist, not a denylist, so a careless future call site drops its
 * property instead of shipping it. Extend alongside each new event — never
 * the error message/description, which routinely quotes client content.
 */
const EVENT_PROPERTIES = new Set(['outcome', 'error_code']);

/**
 * Super properties registered once via `posthog.register` (see
 * `registerGlobalProperties`/`registerRoles`) and expected on every event —
 * including our own named ones. Without this set, `allowlistProperties`
 * would strip them from custom events the same way it strips everything
 * else not explicitly named, even though they ride on `$`-prefixed events
 * for free. `product` in particular is load-bearing: this PostHog project
 * is shared with other products, so a custom event without it can't be told
 * apart from another product's event of the same name.
 */
const SCHEMA_PROPERTIES = new Set(['product', 'environment', 'account_id', 'user_role']);

const TRANSPORT_PROPERTIES = new Set([
  'token',
  'distinct_id',
  '$device_id',
  '$anon_distinct_id',
  '$process_person_profile',
  '$session_id',
  '$window_id',
  '$insert_id',
  '$time',
  '$lib',
  '$lib_version',
  '$config_defaults',
]);

export const allowlistProperties = (event) => {
  if (!event) {
    return null;
  }

  event.properties = Object.fromEntries(
    Object.entries(event.properties ?? {}).filter(
      ([key]) => TRANSPORT_PROPERTIES.has(key) || SCHEMA_PROPERTIES.has(key) || EVENT_PROPERTIES.has(key),
    ),
  );

  return event;
};

/** The `before_send` hook: the last thing that runs before an event leaves the browser. */
export const sanitizeEvent = (event) => {
  if (!event) {
    return null;
  }

  sanitizeEventLevelPersonProperties(event);

  // Our own events declare what they carry, so they get the strict
  // allowlist rather than URL templating.
  if (typeof event.event === 'string' && !event.event.startsWith('$')) {
    return allowlistProperties(event);
  }

  const properties = sanitizeUrlProperties(event.properties ?? {});

  if (Array.isArray(properties.$elements)) {
    properties.$elements = sanitizeElements(properties.$elements);
  }

  event.properties = properties;
  return event;
};

// ---------------------------------------------------------------------------
// 2. THE CLIENT
// ---------------------------------------------------------------------------

/**
 * posthog-js is tens of KB gzipped and would otherwise sit in the
 * render-blocking entry chunk, so it's dynamically imported instead of
 * statically — the SDK downloads in parallel with first render rather than
 * ahead of it. Nothing below touches `posthog` before `initialized` is true,
 * and `startIfEnabled` defers itself until the module has landed, so the
 * public API stays synchronous and every entry point keeps no-opping until
 * tracking has actually started.
 */
let posthog = null;
const posthogReady = import('posthog-js').then(
  (module) => {
    posthog = module.default;
  },
  (error) => {
    console.warn('[tracking] could not load the analytics SDK; continuing without it', error);
  },
);

let initialized = false;
let identifyGeneration = 0;
// `identifyUser` is called from a route guard, so it runs on every protected
// navigation, not just once at login — skip the rehash when the email hasn't
// changed rather than re-identifying (and re-hashing) on every route change.
let identifiedEmail = null;
// Tracked separately from `identifiedEmail`: the route guard's `roles` can
// start incomplete and grow once `authStore/FetchUserData` resolves (it reads
// org-feature-derived roles from the profile endpoint, not just the JWT
// claim), so registration must not be skipped just because the email hasn't
// changed.
let registeredRolesKey = null;
// Unknown until the first `identifyUser` call after login; `isEnabled()`
// treats "unknown" the same as "not Anthill's org" so tracking stays inert
// until proven otherwise, rather than assuming yes.
let currentRoles = [];

const PSEUDONYM_SALT = 'arcane-user-pseudonym';

/**
 * Turn the user's email into a stable, one-way pseudonym so the address
 * itself never reaches PostHog. The salt ships in the bundle, so this is
 * pseudonymization, not anonymization — it buys that stored data alone
 * cannot be matched against a list of addresses.
 */
const pseudonymizeEmail = async (email) => {
  const bytes = new TextEncoder().encode(`${PSEUDONYM_SALT}:${email.trim().toLowerCase()}`);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const getToken = () => import.meta.env.VUE_APP_POSTHOG_PROJECT_ID?.trim();
const getHost = () => import.meta.env.VUE_APP_POSTHOG_URL?.trim();

/** The org subdomain, the same way `plugins/authClient.js` reads it. */
const getOrgSlug = () => {
  const host = window.location.host.split('.')[0];
  return ['dev', 'staging', 'arcane', 'localhost:3000'].includes(host) ? null : host;
};

const registerGlobalProperties = () => {
  const stage = import.meta.env.VUE_APP_STAGE;
  const orgSlug = getOrgSlug();

  posthog.register({
    product: 'Arcane',
    environment: stage && stage !== 'PROD' ? 'staging' : 'production',
    ...(orgSlug ? { account_id: orgSlug } : {}),
  });
};

const ENABLED_STAGES = new Set(['STG', 'PROD']);

/**
 * Org-level allowlist, same shape as `useSecondOrderReferencesGating`:
 * true only for orgs carrying `feature:posthog_tracking` (Anthill's own
 * tenants, not customers). ANDed into `isEnabled()` below rather than
 * checked after the fact, so `posthog.init()` never runs at all for an
 * org that doesn't have it — mirrors how rosalind ANDs its
 * backend-delivered `features.posthog.enabled` into its own `isEnabled()`.
 */
const isAllowedOrgTracking = (roles) => hasAnyRole(roles, `feature:${RESOURCE_TYPE.POSTHOG_TRACKING}`);

const isEnabled = () =>
  Boolean(
    ENABLED_STAGES.has(import.meta.env.VUE_APP_STAGE) &&
      isAllowedOrgTracking(currentRoles) &&
      getToken() &&
      getHost(),
  );

/**
 * Register the `user_role` super property, independent of `identifyUser`'s
 * once-per-session dedupe. Cheap (no hashing), so it's safe to call on every
 * route-guard pass — only actually calls into posthog-js when the role set
 * has changed since the last registration.
 */
const registerRoles = (roles) => {
  if (!roles?.length) {
    return;
  }

  const key = [...roles].sort().join(',');
  if (key === registeredRolesKey) {
    return;
  }

  registeredRolesKey = key;
  posthog.register({ user_role: roles });
};

/**
 * Start PostHog if configured. Safe to call repeatedly — a no-op once
 * `initialized`, and a no-op at app boot for any org until `identifyUser`
 * supplies roles and calls this again, since `isEnabled()` needs those
 * roles to know whether the org is Anthill's own. Tracking is
 * non-critical, so a failing init is swallowed with a warning — analytics
 * must never break app bootstrap.
 */
const startIfEnabled = () => {
  if (initialized || !isEnabled()) {
    return;
  }

  // The SDK is still downloading: retry once it lands (and only if it did —
  // a failed load leaves `posthog` null and tracking permanently inert).
  if (!posthog) {
    posthogReady.then(() => posthog && startIfEnabled());
    return;
  }

  try {
    posthog.init(getToken(), {
      api_host: getHost(),
      defaults: '2026-05-30',

      // Anonymous until login, then the salted digest of the email (see
      // `identifyUser`). Must stay `'identified_only'`, never `'never'` —
      // under that value posthog-js silently ignores `identify`.
      person_profiles: 'identified_only',

      autocapture: {
        element_attribute_ignorelist: [
          'aria-label',
          'title',
          'alt',
          'placeholder',
          'href',
          'value',
          'name',
          'src',
          'srcset',
          'modelvalue',
        ],
      },
      capture_pageview: 'history_change',
      capture_pageleave: true,

      capture_heatmaps: false,
      capture_dead_clicks: false,
      capture_performance: false,
      capture_exceptions: false,

      // Element text is where this app's customer content lives (document
      // titles, prompts, generated copy). Masked at source, since autocapture
      // emits the element twice and only the `$elements` array (handled in
      // sanitizeElements above) can be edited by key.
      mask_all_text: true,

      // Off, and not configurable: replay records rendered page text, which
      // inside the authoring surfaces IS the client's content.
      disable_session_recording: true,

      disable_surveys: true,
      disable_surveys_automatic_display: true,
      disable_product_tours: true,
      disable_conversations: true,

      // No remote config, no `/flags` request — PostHog cannot change how
      // this SDK behaves without us releasing.
      advanced_disable_flags: true,
      advanced_disable_decide: true,
      advanced_disable_feature_flags: true,
      advanced_disable_feature_flags_on_first_load: true,

      // Blocks every PostHog-served remote script (rrweb included).
      disable_external_dependency_loading: true,

      save_campaign_params: false,
      save_referrer: false,
      disable_capture_url_hashes: true,

      before_send: sanitizeEvent,

      // Closes the `?__posthog_debug=true` escape hatch.
      debug: false,
    });

    initialized = true;
    registerGlobalProperties();
  } catch (error) {
    console.warn('[tracking] init failed; continuing without it', error);
  }
};

/** Call once from main.js, before mounting the app. */
export const initializeTracking = () => {
  startIfEnabled();
};

/**
 * Record that something happened. A no-op until tracking has started, so
 * callers never need to check.
 *
 * @param {string} eventName - snake_case `object_verb`, a fixed string
 *   literal. Never interpolated — that creates a new event definition per
 *   value and PostHog rate-limits projects with too many.
 * @param {object} [properties] - Low-cardinality descriptors. Anything not
 *   in EVENT_PROPERTIES above is dropped before sending.
 */
export const trackEvent = (eventName, properties) => {
  if (!initialized) {
    return;
  }

  posthog.capture(eventName, properties);
};

/**
 * Report the outcome of a backend API call as a named event, the way an
 * `endpoint.trackEvent` tag in `api-gateway-endpoints.js` requests it.
 *
 * Purpose-built like `trackElementAdded`/`trackContentDrop`, so the gateway's
 * response/error interceptor — the one place outside this file that reports
 * outcomes — never has to call the untyped `trackEvent` itself. `eventName`
 * is a fixed string literal from an endpoint definition, never caller-built.
 *
 * @param {string} eventName - The event to report.
 * @param {object} outcome
 * @param {boolean} outcome.success - Whether the call succeeded.
 * @param {number|null} [outcome.errorStatus] - The HTTP status on failure.
 */
export const trackApiOutcome = (eventName, { success, errorStatus = null }) => {
  if (!eventName) {
    return;
  }

  trackEvent(eventName, success ? { outcome: 'success' } : { outcome: 'error', error_code: errorStatus });
};

/**
 * Call after login, once the user's email and roles are known. The email is
 * pseudonymized first — the address must never reach PostHog. Fails closed:
 * a hashing error leaves events anonymous rather than falling back to the
 * address.
 */
export const identifyUser = async (email, roles) => {
  if (!email) {
    return;
  }

  // `isEnabled()` needs roles to decide if this is Anthill's own org, so
  // stash them before retrying `startIfEnabled()` — a no-op if it already
  // ran, otherwise the first point at which init can actually happen.
  currentRoles = roles || [];
  startIfEnabled();

  if (!initialized) {
    return;
  }

  // Independent of the identify dedupe below: roles can arrive incomplete on
  // the first route-guard pass and grow once FetchUserData resolves, and
  // must not get stuck at whatever was known on that first pass.
  registerRoles(roles);

  if (email === identifiedEmail) {
    return;
  }

  const generation = identifyGeneration;

  let pseudonym;
  try {
    pseudonym = await pseudonymizeEmail(email);
  } catch (error) {
    console.warn('[tracking] pseudonymization failed; staying anonymous', error);
    return;
  }

  if (generation !== identifyGeneration) {
    return; // resetUser ran while hashing — this identity no longer applies
  }

  posthog.identify(pseudonym);
  identifiedEmail = email;
};

/** Call on logout, so the next person to use the browser doesn't inherit the last one's identity. */
export const resetUser = () => {
  identifyGeneration += 1;
  identifiedEmail = null;
  registeredRolesKey = null;

  if (initialized) {
    posthog.reset();
    // reset() clears all super properties, including the environment-level
    // ones that are still valid for the next session in this browser.
    registerGlobalProperties();
  }
};
